// File: frontend/src/components/ui/Disclosure.js
//
// A SECTION THAT OPENS AND CLOSES — AND A KEYBOARD CAN OPEN IT.
//
// The Router page's collapsible cards used a clickable <Box> as their header:
// no role, no tabIndex, no aria-expanded. DHCP Leases, Per-Interface Breakdown
// and Traffic Predictor started collapsed, so a keyboard or screen-reader user
// could never reach what was inside them. This is the one disclosure pattern
// for the product: a real <button> (Enter and Space for free) inside a heading,
// aria-expanded / aria-controls on the button, a labelled region for the panel,
// a visible focus ring, and reduced motion honoured.
//
//   <Disclosure id="dhcp" title="DHCP leases" summary="Read on demand">…</Disclosure>

import React, { useState } from "react";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { useReducedMotion, duration } from "./motion";
import { MIN_TAP } from "./tokens";

/**
 * @param {string}  id            unique on the page; names the button and panel
 * @param {node}    title
 * @param {node}    [summary]     one line beside the title (a count, a state)
 * @param {boolean} [defaultOpen]
 * @param {boolean} [open]        controlled mode, with onToggle
 * @param {Function}[onToggle]    (nextOpen) => void
 * @param {number}  [headingLevel] 2–6, default 3
 * @param {node}    [actions]     controls placed beside the header, outside the button
 */
export default function Disclosure({
  id,
  title,
  summary,
  defaultOpen = false,
  open: controlledOpen,
  onToggle,
  headingLevel = 3,
  actions,
  children,
  sx,
}) {
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const open = controlledOpen != null ? controlledOpen : innerOpen;
  const reduced = useReducedMotion();
  const buttonId = `${id}-disclosure-button`;
  const panelId = `${id}-disclosure-panel`;
  const level = Math.min(6, Math.max(2, Number(headingLevel) || 3));

  const toggle = () => {
    const next = !open;
    if (controlledOpen == null) setInnerOpen(next);
    onToggle?.(next);
  };

  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Typography component={`h${level}`} sx={{ m: 0, flex: 1, minWidth: 0, fontSize: "inherit", fontWeight: "inherit" }}>
          <ButtonBase
            id={buttonId}
            aria-expanded={open ? "true" : "false"}
            aria-controls={panelId}
            onClick={toggle}
            sx={{
              width: "100%", minHeight: MIN_TAP, px: 1, mx: -1, borderRadius: 2,
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5,
              textAlign: "left",
              "&:hover": { bgcolor: "action.hover" },
              "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box component="span" sx={{ display: "block", fontSize: 15, fontWeight: 700, color: "text.primary", lineHeight: 1.35 }}>
                {title}
              </Box>
              {summary != null && (
                <Box component="span" sx={{ display: "block", fontSize: 12.5, color: "text.secondary", lineHeight: 1.45, mt: 0.15 }}>
                  {summary}
                </Box>
              )}
            </Box>
            <ExpandMoreIcon
              aria-hidden
              sx={{
                color: "text.secondary", flexShrink: 0,
                transform: open ? "rotate(180deg)" : "none",
                transition: reduced ? "none" : "transform 150ms ease",
              }}
            />
          </ButtonBase>
        </Typography>
        {actions ? <Box sx={{ flexShrink: 0 }}>{actions}</Box> : null}
      </Box>
      <Collapse in={open} timeout={duration(reduced, "base")} unmountOnExit>
        <Box id={panelId} role="region" aria-labelledby={buttonId} sx={{ pt: 1.25, minWidth: 0 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
